import { Component, OnInit } from '@angular/core';
import { Store } from '@ngrx/store';
import { State } from '../../../../core/store';
import { Actions, ofType } from '@ngrx/effects';
import { tap } from 'rxjs';
import { UntypedFormArray, UntypedFormBuilder, FormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { BaseComponent } from '../../../../core/abstracts/baseComponent';
import { CeuEventCertificationHasCeuModel, CeuEventHasCertificationTypeModel, CeuEventHasUserModel, CeuEventModel } from '../../../../models/certificationModel';
import { deepClone, getAllControlsErrors, markFormGroupTouched, updateFormGroupValidity } from '../../../../helpers/utils';
import { closeEditCeuEvent, getCeuEvent, loadInstructors, saveCeuEvent, saveCeuEventSuccess, updateCeuEventForm } from '../../../../core/store/certification/certification.action';
import { formatDate } from '@angular/common';
import { loadEmployeeUsers } from '../../../../core/store/employee/employee.action';
import { ModifiedSelectItem, UserAutoCompleteItem } from '../../../../models/utilModels';

@Component({
    selector: 'app-edit-ceu-event',
    templateUrl: './edit-ceu-event.component.html',
    styleUrls: ['./edit-ceu-event.component.scss'],
    standalone: false
})
export class EditCeuEventComponent extends BaseComponent implements OnInit {
  display: boolean = false;
  ceuEvent: CeuEventModel;
  title: string;
  form: UntypedFormGroup;
  disableButtons: boolean = true;
  campusId: number = 0;
  showOnlyActiveEmployees: boolean = true;
  showOnlyCurrentInstructors: boolean = true;
  eventId: number = 0;

  ceuCategories: ModifiedSelectItem[];
  certificationTypeOptions: ModifiedSelectItem[];
  ceuTypes: ModifiedSelectItem[];
  selectedUsers: ModifiedSelectItem[];
  userEmployeeList: UserAutoCompleteItem[];
  instructorList: UserAutoCompleteItem[];
  selectedCertifications: ModifiedSelectItem[];

  constructor(
    private actions$: Actions,
    private store: Store<State>,
    private fb: UntypedFormBuilder) {
    super();
  }

  ngOnInit(): void {
    this.form = this.createForm();
    this.subs.push(
      this.store.select(s => s.certificationState.editingCeuEvent).subscribe(editingCeu => {
        if (this.display && !editingCeu || (editingCeu && this.eventId == 0)) {
          this.resetForm();          
        }
        this.display = editingCeu;
                
      }),
      this.store.select(s => s.certificationState.savingCeuEvent).subscribe(savingCeu => {
        this.disableButtons = savingCeu;
      }),
      this.store.select(s => s.certificationState.ceuEventId).subscribe(id => {        
        this.eventId = id;
        this.title = "New CEU Event";
        if (typeof id !== 'undefined' && id > 0) {
          this.store.dispatch(getCeuEvent({ ceuEventId: id }));
        }        
      }),
      this.store.select(s => s.certificationState.selectedCeuEvent).subscribe(ceu => {
        if (ceu === null) {
          this.ceuEvent = null;
          return;
        }
        this.title = "Edit CEU Event #" + ceu.ceuEventId;
        this.form.get("ceuEventId").setValue(ceu.ceuEventId);
        this.form.get("eventName").setValue(ceu.eventName);
        this.form.get("modality").setValue(ceu.modality != null ? ceu.modality : "");
        this.form.get("notes").setValue(ceu.notes != null ? ceu.notes : "");
        this.form.get("startDateString").setValue(ceu.startDateString);
        this.form.get("endDateString").setValue(ceu.endDateString);
        this.form.get("instructorId").setValue(ceu.instructorId);
        this.form.get("selectedInstructor").patchValue({
          name: ceu.selectedInstructor.label,
          id: ceu.selectedInstructor.value
        });
        this.resetUploadedFiles();
        if (ceu.eventUsers != null && ceu.eventUsers.length > 0) {
          this.resetEventUsers(false);
          ceu.eventUsers.forEach(u => {
            this.addUserDataToEvent(u);
          });

        } else {
          this.resetEventUsers(true);
        }
        if (ceu.certificationTypes != null && ceu.certificationTypes.length > 0) {
          this.resetCertificationTypes(false);
          ceu.certificationTypes.forEach(c => {
            this.addCertificationDataToEvent(c);
          })

        } else {
          this.resetCertificationTypes(true);
        }

        this.resetUploads();
        if (ceu.uploads != null && ceu.uploads.length > 0) {
          
          let uploads = this.form.get('uploads') as UntypedFormArray;          
          ceu.uploads.forEach(u => {
            uploads.push(this.fb.group({
              id: this.fb.control(u.id),
              fileName: this.fb.control(u.fileName),
              fileAwsBucket: this.fb.control(u.fileAwsBucket),
              fileAwsKey: this.fb.control(u.fileAwsKey)              
            }));
          });
        } 
      }),
      this.store.select(s => s.certificationState.ceuCategories).subscribe(categories => {
        if (categories && categories.length > 0) {
          this.ceuCategories = categories.map(x => ({ label: x.label, value: x.value }));
          this.ceuCategories.unshift({ label: 'Select Type', value: 0 });
        }
        else {
          this.ceuCategories = [{ label: 'loading...', value: undefined }];
        }
      }),
      this.store.select(s => s.certificationState.ceuTypes).subscribe(types => {
        if (types && types.length > 0) {
          this.ceuTypes = types.map(x => ({ label: x.label, value: x.value }));
          this.ceuTypes.unshift({ label: 'Select Type', value: 0 });
        }
        else {
          this.ceuTypes = [{ label: 'loading...', value: undefined }];
        }
      }),
      this.store.select(s => s.certificationState.certificationTypes).subscribe(types => {
        if (types && types.length > 0) {
          this.certificationTypeOptions = types.map(x => ({ label: x.label, value: x.value }));          
        }
        else {
          this.certificationTypeOptions = [{ label: 'loading...', value: undefined }]
        }
      }),
      this.store.select(s => s.employeeState.employeeUsers).subscribe(users => {
        this.userEmployeeList = users.map(x => ({
          id: x.value,
          name: x.label
        }));
      }),
      this.store.select(s => s.certificationState.instructors).subscribe(instructors => {
        this.instructorList = instructors.map(x => ({
          id: x.value,
          name: x.label
        }));
      }),
      this.actions$.pipe(
        ofType(saveCeuEventSuccess),
        tap(action => {
          this.closeModal();
        })
      ).subscribe(),
    );
  }

  createForm() {
    let f = this.fb.group({
      ceuEventId: this.fb.control(0),
      eventName: this.fb.control("", [Validators.required]),
      modality: this.fb.control(""),
      notes: this.fb.control(""),
      startDateString: this.fb.control(formatDate(new Date(), 'yyyy-MM-dd', 'en-US'), [Validators.required]),
      endDateString: this.fb.control(formatDate(new Date(), 'yyyy-MM-dd', 'en-US'), [Validators.required]),
      instructorId: this.fb.control(0, [Validators.required]),
      selectedInstructor: this.fb.control({
        name: "",
        id: 0
      }),
      eventUsers: this.fb.array([this.fb.group({
        ceuEventHasUserId: this.fb.control(0),
        eventId: this.fb.control(0),
        employeeName: this.fb.control(""),
        userId: this.fb.control(0),
        selectedUser: this.fb.control({
          id: 0,
          name: ""
        })
      })]),
      certificationTypes: this.fb.array([this.fb.group({
        ceuEventHasCertificationTypeId: this.fb.control(0),
        eventId: this.fb.control(0),
        certificationTypeId: this.fb.control(0),
        cEUs: this.fb.array([this.fb.group(
          {
            ceuEventCertificationHasCeuId: this.fb.control(0),
            eventHasCertificationTypeId: this.fb.control(0),
            ceuCategoryId: this.fb.control(0),
            ceuTypeId: this.fb.control(0),
            units: this.fb.control(0),
          })
        ])
      })]),
      uploadedFiles: this.fb.array([]),
      uploads: this.fb.array([])
    });
    
    return f;
  }

  resetForm() {
    this.form.reset({
      ceuEventId: 0,
      startDateString: formatDate(new Date(), 'yyyy-MM-dd', 'en-US'),
      endDateString: formatDate(new Date(), 'yyyy-MM-dd', 'en-US'),      
    });
    this.resetEventUsers(true);
    this.resetCertificationTypes(true);
    this.resetUploadedFiles();
    this.resetUploads();
  }

  resetUploads() {
    let files = this.form.get('uploads') as UntypedFormArray;
    files.clear();
  }

  resetUploadedFiles() {
    let files = this.form.get('uploadedFiles') as UntypedFormArray;
    files.clear();
  }

  resetEventUsers(addEmpty) {
    let eventUsers = this.form.get('eventUsers') as UntypedFormArray;
    eventUsers.clear();
    if (addEmpty) {
      this.addUserToEvent();
    }
  }

  resetCertificationTypes(addEmpty) {
    let certifications = this.form.get('certificationTypes') as UntypedFormArray;
    certifications.clear();
    if (addEmpty) {
      this.addCertificationToEvent();
    }
  }

  getStaffList(event) {
    this.store.dispatch(loadEmployeeUsers({
      prefixText: event.query,
      programId: this.campusId,
      payrollClassificationId: null,
      showTerminated: !this.showOnlyActiveEmployees,
      showInactive: !this.showOnlyActiveEmployees
    }));
  }

  setUserAfterStaffChange(selectedUser, index) {
    if (typeof selectedUser.value.id !== 'number') {
      return;
    }

    let eventUsers = this.form.get('eventUsers') as UntypedFormArray;
    let userToUpdate = eventUsers.at(index) as UntypedFormGroup;
    userToUpdate.patchValue({
      employeeName: selectedUser.value.name,
      userId: selectedUser.value.id
    });   
  }

  clearUserAfterClear(event, index) {
    
    let eventUsers = this.form.get('eventUsers') as UntypedFormArray;
    let userToUpdate = eventUsers.at(index) as UntypedFormGroup;
    userToUpdate.patchValue({
      employeeName: "",
      userId: 0
    });
  }

  setCertificationTypeAfterChange(event, index) {
    if (typeof event.value !== 'number') {
      return;
    }

    let certifications = this.form.get('certificationTypes') as UntypedFormArray;
    let certType = certifications.at(index) as UntypedFormGroup;
    certType.patchValue({
      certificationTypeId: event.value
    });       
  }

  setCategoryAfterChange(event, parentIndex, index) {
    if (typeof event.value !== 'number') {
      return;
    }

    let certifications = this.form.get('certificationTypes') as UntypedFormArray;
    let certification = certifications.at(parentIndex) as UntypedFormGroup;
    let ceus = certification.get('cEUs') as UntypedFormArray;
    let ceu = ceus.at(index);
    ceu.patchValue({
      ceuCategoryId: event.value
    });
  }

  setTypeAfterChange(event, parentIndex, index) {
    if (typeof event.value !== 'number') {
      return;
    }

    let certifications = this.form.get('certificationTypes') as UntypedFormArray;
    let certification = certifications.at(parentIndex) as UntypedFormGroup;
    let ceus = certification.get('cEUs') as UntypedFormArray;
    let ceu = ceus.at(index);
    ceu.patchValue({
      ceuTypeId: event.value
    });
  }

  getInstructorList(event) {
    this.store.dispatch(loadInstructors({
      prefixText: event.query,
      certificationTypeId: 0,
      showArchived: !this.showOnlyCurrentInstructors,
      showTerminated: !this.showOnlyCurrentInstructors,
      showInactive: !this.showOnlyCurrentInstructors
    }));
  }

  setInstructorAfterInstructorChange(selectedInstructor) {
    if (typeof selectedInstructor.value.id !== 'number') {
      return;
    }

    this.form.get('instructorId').setValue(selectedInstructor.value.id);
  }

  clearInstructorAfterClear(event) {    

    this.form.get('instructorId').setValue(0);
  }

  closeModal() {
    this.store.dispatch(closeEditCeuEvent());
  }

  saveEventEdits() {    
    updateFormGroupValidity(this.form);
    if (this.form.valid) {           
      this.store.dispatch(saveCeuEvent({ ceuEvent: this.form.value as CeuEventModel }));
    } else {
      markFormGroupTouched(this.form);
    }
  }

  addUserToEvent() {    
    let eventUsers = this.form.get('eventUsers') as UntypedFormArray;
    eventUsers.push(this.fb.group({
      ceuEventHasUserId: this.fb.control(0),
      eventId: this.fb.control(0),
      employeeName: this.fb.control(""),
      userId: this.fb.control(0),
      selectedUser: this.fb.control({
        id: 0,
        name: ""
      })
    }));    
    return false;
  }

  addUserDataToEvent(userEvent: CeuEventHasUserModel) {
    if (userEvent == null) {
      return;
    }

    let eventUsers = this.form.get('eventUsers') as UntypedFormArray;
    eventUsers.push(this.fb.group({
      ceuEventHasUserId: this.fb.control(userEvent.ceuEventHasUserId),
      eventId: this.fb.control(userEvent.eventId),
      employeeName: this.fb.control(userEvent.employeeName),
      userId: this.fb.control(userEvent.userId),
      selectedUser: this.fb.control({
        id: userEvent.userId,
        name: userEvent.employeeName
      })
    }));
    return false;
  }

  removeUserFromEvent(index) {
    let eventUsers = this.form.get('eventUsers') as UntypedFormArray;
    eventUsers.removeAt(index);
    return false;
  }

  addCertificationToEvent() {
    let certifications = this.form.get('certificationTypes') as UntypedFormArray;
    certifications.push(this.fb.group({
      ceuEventHasCertificationTypeId: this.fb.control(0),
      eventId: this.fb.control(0),
      certificationTypeId: this.fb.control(0),
      cEUs: this.fb.array([this.fb.group(
        {
          ceuEventCertificationHasCeuId: this.fb.control(0),
          eventHasCertificationTypeId: this.fb.control(0),
          ceuCategoryId: this.fb.control(0),
          ceuTypeId: this.fb.control(0),
          units: this.fb.control(0),         
        })
      ])
    }));
    return false;    
  }

  addCertificationDataToEvent(data: CeuEventHasCertificationTypeModel) {
    if (data == null) {
      return;
    }
    let certifications = this.form.get('certificationTypes') as UntypedFormArray;
    certifications.push(this.fb.group({
      ceuEventHasCertificationTypeId: this.fb.control(data.ceuEventHasCertificationTypeId),
      eventId: this.fb.control(data.eventId),
      certificationTypeId: this.fb.control(data.certificationTypeId),
      cEUs: this.fb.array([...data.ceUs.map(c => this.fb.group(
        {
          ceuEventCertificationHasCeuId: this.fb.control(c.ceuEventCertificationHasCeuId),
          eventHasCertificationTypeId: this.fb.control(c.eventHasCertificationTypeId),
          ceuCategoryId: this.fb.control(c.ceuCategoryId),
          ceuTypeId: this.fb.control(c.ceuTypeId != null ? c.ceuTypeId : 0),
          units: this.fb.control(c.units),
        }))
      ])
    }));
    return false;
  }

  removeCertificationFromEvent(index) {
    let certifications = this.form.get('certificationTypes') as UntypedFormArray;
    certifications.removeAt(index);
    return false;
  }      

  removeCeu(parentIndex, index) {
    let certifications = this.form.get('certificationTypes') as UntypedFormArray;
    let certification = certifications.at(parentIndex) as UntypedFormGroup;
    let ceus = certification.get('cEUs') as UntypedFormArray;
    ceus.removeAt(index);
    return false;
  }

  addCeuToCertification(index) {
    let certifications = this.form.get('certificationTypes') as UntypedFormArray;
    let certification = certifications.at(index) as UntypedFormGroup;
    let ceus = certification.get('cEUs') as UntypedFormArray;
    ceus.push(this.fb.group(
      {
        ceuEventCertificationHasCeuId: this.fb.control(0),
        eventHasCertificationTypeId: this.fb.control(0),
        ceuCategoryId: this.fb.control(0),
        ceuTypeId: this.fb.control(0),
        units: this.fb.control(0),
      }));
    return false;
  }

  removeUploadFromEvent(index) {
    let uploads = this.form.get('uploads') as UntypedFormArray;
    uploads.removeAt(index);
    return false;
  }

  removeUploadedFileFromEvent(index) {
    let uploads = this.form.get('uploadedFiles') as UntypedFormArray;
    uploads.removeAt(index);
    return false;
  }

  changedFile(fileInput) {
    let files = Object.assign([], fileInput.files);

    let uploadedFiles = this.form.get('uploadedFiles') as UntypedFormArray;
    files.forEach(f => {
      uploadedFiles.push(this.fb.control(f));
    });
    return false;
  }

  getUploads() {
    return (this.form.get('uploads') as UntypedFormArray).controls;
  }

  getUploadedFiles() {
    return (this.form.get('uploadedFiles') as UntypedFormArray).controls;
  }

  getCertificationTypes() {
    return (this.form.get('certificationTypes') as UntypedFormArray).controls;
  }

  getSubCertificationSections(i: number) {
    let certifications = this.form.get('certificationTypes') as UntypedFormArray;
    let selectedCert = certifications.at(i) as UntypedFormGroup;
    return (selectedCert.get('cEUs') as UntypedFormArray).controls;
  }

  getEventUsers() {
    return (this.form.get('eventUsers') as UntypedFormArray).controls;
  }
}
